import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DatePicker, Flex, type TimeRangePickerProps } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { useMobileMediaQuery } from '@/shared/lib/hooks';
import type { RangeValueType } from '@/shared/types/antd';

import { useTradingJournalDateRange } from '../../lib/use-trading-journal-date-range';

import cls from './trading-platform-date-range-picker.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';

interface TradingPlatformDateRangePickerProps {
    isLoading: boolean;
}

export const TradingPlatformDateRangePicker = memo(
    (props: TradingPlatformDateRangePickerProps) => {
        const { isLoading } = props;

        const { t } = useTranslation('trading-journal');

        const isMobile = useMobileMediaQuery();

        const { dateRange, setDateRange } = useTradingJournalDateRange();

        const defaultValue = useMemo<RangeValueType>(() => {
            const [startDate, endDate] = dateRange;

            return [dayjs(startDate), dayjs(endDate)];
        }, [dateRange]);

        const onOk = useCallback(
            (value: RangePickerProps['value']) => {
                if (value) {
                    const [startDate, endDate] = value;

                    setDateRange([
                        startDate?.format(DATE_FORMAT),
                        endDate?.format(DATE_FORMAT),
                    ]);
                } else {
                    setDateRange(null);
                }
            },
            [setDateRange],
        );

        const rangePresets: TimeRangePickerProps['presets'] = useMemo(
            () => [
                {
                    label: t('tradingJournalRangePickerPresetToday'),
                    value: [dayjs(), dayjs()],
                },
                {
                    label: t('tradingJournalRangePickerPresetThisWeek'),
                    value: [dayjs().startOf('week'), dayjs().endOf('week')],
                },
                {
                    label: t('tradingJournalRangePickerPresetLast30Days'),
                    value: [dayjs().add(-30, 'd'), dayjs()],
                },
                {
                    label: t('tradingJournalRangePickerPresetLastMonth'),
                    value: [
                        dayjs().add(-1, 'M').startOf('month'),
                        dayjs().add(-1, 'M').endOf('month'),
                    ],
                },
                {
                    label: t('tradingJournalRangePickerPresetYearToDate'),
                    value: [dayjs().startOf('year'), dayjs()],
                },
            ],
            [t],
        );

        const handlePresetClick = useCallback(
            (presetValue: [Dayjs, Dayjs]) => {
                const [startDate, endDate] = presetValue;

                setDateRange([
                    startDate.format(DATE_FORMAT),
                    endDate.format(DATE_FORMAT),
                ]);
            },
            [setDateRange],
        );

        const renderMobilePresets = useCallback(
            () => (
                <Flex
                    className={cls.mobilePresets}
                    gap={8}
                    wrap="wrap"
                >
                    {rangePresets.map((preset) => (
                        <Button
                            key={String(preset.label)}
                            onClick={() =>
                                handlePresetClick(
                                    preset.value as [Dayjs, Dayjs],
                                )
                            }
                            size="small"
                            type="default"
                        >
                            {preset.label}
                        </Button>
                    ))}
                </Flex>
            ),
            [rangePresets, handlePresetClick],
        );

        return (
            <DatePicker.RangePicker
                allowClear={false}
                defaultValue={defaultValue}
                disabled={isLoading}
                needConfirm={isMobile}
                onChange={onOk}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(!isMobile && { presets: rangePresets })}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isMobile && { renderExtraFooter: renderMobilePresets })}
                rootClassName={cls.root}
                size="large"
            />
        );
    },
);

TradingPlatformDateRangePicker.displayName = 'TradingPlatformDateRangePicker';
